import { useState } from 'react'
import type { OperationsModule, OperationsModuleStatus, OperationsAction } from './operationsModules'
import type { LiveQueueState } from './liveQueues'
import { CommandCenterShell } from '../shell'
import './operations-surface.css'

/**
 * OperationsMiniPortal — the shared, workable Operations mini-portal shell.
 *
 * PHASE_2G: renders its header/nav/breadcrumb/wide-frame chrome via the shared
 * CommandCenterShell, and supplies the Operations-specific working surface as slots:
 * a context bar (purpose + local quick-add), a left work surface (queue, primary
 * actions, working log), and a right rail (status/readiness, blockers, receipts,
 * next actions).
 *
 * CHAT #3 conversion: the work-surface + right-rail content roots adopt the
 * shared `.sqv-surface` foundation (canonical #0b0d17) and all presentation
 * moved into operations-surface.css. The only inline styles are the DYNAMIC
 * per-portal accent (--sqv-accent). Behavior is unchanged from #99.
 *
 * Honesty: the working log + quick-add are LOCAL and non-persistent (labelled).
 * Queue items are only ever Template/Example/Suggested. Nothing here fakes a
 * saved record or a live backend. Presentational + SSR-safe.
 */
export interface OperationsMiniPortalProps {
  module?: OperationsModule
  /**
   * When provided, replaces the static registry-driven queue with a real,
   * live-fetched one (honest loading/error/authRequired/empty states).
   * Modules without a live queue keep the existing static behavior.
   */
  liveQueue?: LiveQueueState
  /** Navigate to a real internal route (primary/related actions). */
  onOpenRoute?: (to: string) => void
  /** Back to the V2 command center (/square/command-center). */
  onBackCommandCenter?: () => void
  /** Back to the Operations home (/square/command-center/operations). */
  onBackOperations?: () => void
  /** Q home — the main Square dashboard (/square/dashboard). */
  onHome?: () => void
}

export const OPERATIONS_STATUS_META: Record<OperationsModuleStatus, { label: string }> = {
  OPEN: { label: 'OPEN' },
  WORKSPACE_READY: { label: 'WORKSPACE READY' },
  ROUTE_READY: { label: 'ROUTE READY' },
  PLANNED: { label: 'PLANNED' },
  SETUP_REQUIRED: { label: 'SETUP REQUIRED' },
  BACKEND_DEFERRED: { label: 'BACKEND DEFERRED' },
  INTEGRATION_DEFERRED: { label: 'INTEGRATION DEFERRED' },
}

export function StatusBadge({ status, size = 'md' }: { status: OperationsModuleStatus; size?: 'sm' | 'md' }) {
  const meta = OPERATIONS_STATUS_META[status]
  return <span className="sqv-ops-chip" data-status={status} data-size={size}>{meta.label}</span>
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="sqv-ops-section">
      <h3 className="sqv-ops-section__title">{title}</h3>
      {children}
    </section>
  )
}

function ActionButton({ action, onOpenRoute, onLocal }: { action: OperationsAction; onOpenRoute?: (to: string) => void; onLocal?: (a: OperationsAction) => void }) {
  const disabled = action.kind === 'deferred'
  const run = () => {
    if (action.kind === 'route' && action.to) onOpenRoute?.(action.to)
    else if (action.kind === 'local') onLocal?.(action)
  }
  return (
    <button
      type="button"
      onClick={run}
      disabled={disabled}
      title={action.note}
      className="sqv-ops-action"
      data-disabled={disabled}
    >
      <span className="sqv-ops-action__label">
        {action.label}
        {action.kind === 'route' && <span className="sqv-ops-action__arrow">→</span>}
        {action.kind === 'local' && <span className="sqv-ops-tag">LOCAL</span>}
        {action.kind === 'deferred' && <span className="sqv-ops-tag">DEFERRED</span>}
      </span>
      {action.note && <span className="sqv-ops-action__note">{action.note}</span>}
    </button>
  )
}

function NotFound({ onBackOperations }: { onBackOperations?: () => void }) {
  return (
    <div className="sqv-ops-notfound">
      <div className="sqv-ops-notfound__inner">
        <div className="sqv-ops-notfound__title">Operations module not found</div>
        <p className="sqv-ops-notfound__body">
          That module id isn’t part of the Operations set. No dead end — head back to the Operations home to pick a module.
        </p>
        <button type="button" onClick={onBackOperations} className="sqv-ops-notfound__back">← Operations</button>
      </div>
    </div>
  )
}

export default function OperationsMiniPortal({ module, liveQueue, onOpenRoute, onBackCommandCenter, onBackOperations, onHome }: OperationsMiniPortalProps) {
  const [log, setLog] = useState('')
  const [quick, setQuick] = useState('')

  if (!module) return <NotFound onBackOperations={onBackOperations} />
  const accent = module.accent
  const accentVar = { ['--sqv-accent']: accent } as React.CSSProperties

  const appendLog = (line: string) => {
    if (!line.trim()) return
    setLog((prev) => (prev ? prev + '\n' : '') + '• ' + line.trim())
  }
  const onLocal = (a: OperationsAction) => appendLog(a.label)

  const contextBar = (
    <>
      <span className="sqv-ops-context__purpose">{module.purpose}</span>
      <div className="sqv-ops-context__add">
        <input
          value={quick}
          onChange={(e) => setQuick(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { appendLog(quick); setQuick('') } }}
          placeholder="Add a working note…"
          aria-label="Add a working note to the local log"
          className="sqv-ops-quickadd"
        />
        <button type="button" onClick={() => { appendLog(quick); setQuick('') }} className="sqv-ops-add" style={accentVar}>Add</button>
      </div>
    </>
  )

  const rightRail = (
    <div className="sqv-surface sqv-ops sqv-ops-rail" style={accentVar}>
      <Section title="Status & readiness">
        <div className="sqv-ops-rail__status">
          <StatusBadge status={module.status} />
          <p className="sqv-ops-backend">{module.backendStatus}</p>
          {module.relatedSurface && (
            <button
              type="button"
              onClick={() => onOpenRoute?.(module.relatedSurface!.to)}
              title={module.relatedSurface.legacy ? 'Legacy fallback — temporary until the native command-center workflow is complete. Opens the current /square shell.' : undefined}
              className="sqv-ops-related"
            >
              {module.relatedSurface.label} →
              {module.relatedSurface.legacy && <span className="sqv-ops-legacy-tag">LEGACY FALLBACK</span>}
            </button>
          )}
        </div>
      </Section>

      <Section title="Blockers & setup">
        {module.blockers.length === 0 ? (
          <p className="sqv-ops-empty">No blockers.</p>
        ) : (
          <ul className="sqv-ops-ul">
            {module.blockers.map((b) => <li key={b} className="sqv-ops-li">{b}</li>)}
          </ul>
        )}
      </Section>

      <Section title={module.receipts.title}>
        <p className="sqv-ops-receipts">{module.receipts.emptyLabel}</p>
      </Section>

      <Section title="Next actions">
        <ol className="sqv-ops-ul">
          {module.nextActions.map((n) => <li key={n} className="sqv-ops-li sqv-ops-li--next">{n}</li>)}
        </ol>
      </Section>
    </div>
  )

  return (
    <CommandCenterShell
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      parentBack={{ label: '← Operations', onClick: onBackOperations }}
      breadcrumb={['COMMAND CENTER', 'OPERATIONS', module.label.toUpperCase()]}
      title={module.label}
      titleAccent={accent}
      statusBadge={<StatusBadge status={module.status} />}
      honestyBadge={{
        label: 'WORKING PORTAL · NOT FAKED',
        title: 'This Operations portal is an internal Square Code working area. OPEN/READY actions open real routes; PLANNED / SETUP-REQUIRED / DEFERRED areas are honest — the working log and quick-add are local and not saved. Nothing here is faked.',
      }}
      contextBar={contextBar}
      rightRail={rightRail}
    >
      {/* LEFT — the work surface. */}
      <div className="sqv-surface sqv-ops sqv-ops-work" style={accentVar}>
        <Section title={module.queue.title}>
          {liveQueue ? (
            liveQueue.authRequired ? (
              <p className="sqv-ops-queue-msg">Sign in to the live cockpit to see this queue.</p>
            ) : liveQueue.loading ? (
              <p className="sqv-ops-queue-msg">Loading…</p>
            ) : liveQueue.error ? (
              <p className="sqv-ops-queue-err">{liveQueue.error}</p>
            ) : liveQueue.rows.length > 0 ? (
              <div className="sqv-ops-list">
                {liveQueue.rows.map((row) => (
                  <div key={row.id} className="sqv-ops-row" data-status={row.meta}>
                    <div className="sqv-ops-row__main">
                      <span className="sqv-ops-row__title">{row.title}</span>
                      {row.subtitle && <span className="sqv-ops-row__sub">{row.subtitle}</span>}
                    </div>
                    {row.meta && <span className="sqv-ops-row__meta">{row.meta}</span>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="sqv-ops-queue-msg">{module.queue.emptyLabel}</p>
            )
          ) : module.queue.items && module.queue.items.length > 0 ? (
            <div className="sqv-ops-list">
              {module.queue.items.map((it) => (
                <div key={it.label} className="sqv-ops-row" data-meta={it.meta}>
                  <span className="sqv-ops-kind">{it.kind.toUpperCase()}</span>
                  <span className="sqv-ops-row__title">{it.label}</span>
                  {it.meta && <span className="sqv-ops-row__meta">{it.meta}</span>}
                </div>
              ))}
              <p className="sqv-ops-hint">Templates/suggested structures — not saved records.</p>
            </div>
          ) : (
            <p className="sqv-ops-queue-msg">{module.queue.emptyLabel}</p>
          )}
        </Section>

        <Section title="Primary actions">
          <div className="sqv-ops-actions-grid">
            {module.primaryActions.map((a) => (
              <ActionButton key={a.label} action={a} onOpenRoute={onOpenRoute} onLocal={onLocal} />
            ))}
          </div>
        </Section>

        <Section title="Working log · local, not saved">
          <textarea
            value={log}
            onChange={(e) => setLog(e.target.value)}
            placeholder={module.notesPrompt}
            aria-label="Working log (local, not saved)"
            rows={7}
            className="sqv-ops-textarea"
          />
          <p className="sqv-ops-log-note">This log lives only in your browser this session — persistence is deferred. Nothing is sent or stored.</p>
        </Section>
      </div>
    </CommandCenterShell>
  )
}
